import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../components/AuthContext';
import { useToast } from '../../../components/ToastContext';
import { useOrg } from '../../../components/OrgContext';
import { logAudit } from '../../../hooks/useAudit';
import { adminCreateUser, adminUpdateUser } from '../../../lib/adminApi';
import PageBar from '../../../components/PageBar';
import ListPanel, { revealPanel } from '../../../components/ListPanel';
import { usersCsv, recordExport } from '../../../lib/referenceExports';
import { downloadCsv } from '../../reports/reportData';
import { regionsChanged } from '../../../lib/access';
import PageLoading from '../../../components/PageLoading';

// Settings -> Users & Access.
//
// People on the left, everything about the selected person on the right. Built
// on the Claimsoft users screen, minus its collapsible Personal / Account split.
//
// Two ideas are doing the work here:
//
//   Site access   A role is not a property of a person, it is a property of a
//                 person *in a site*. Someone can be a Site Admin here and a
//                 Rep somewhere else, so roles are a list, not a dropdown.
//
//   Assignments   Regions and warehouses are what a person can reach - and
//                 when a count cycle opens, they are what blank count sheets
//                 get created from. They belong to the site being viewed, so
//                 they sit under it.
//
// A referral source is not assigned here. It hangs off a warehouse, so giving
// somebody the warehouse gives them its referral source with it. Accounts are
// not assigned here either - nothing in the counting path reads them any more.
//
// Global Admin spans every site, so it is not in the per-site role list. It is
// its own switch, and only another Global Admin can move it.
//
// Every permission check is in the database. This screen cannot grant access
// its user isn't entitled to grant, whatever it sends.

const SITE_ROLES = [
  { value: 'admin',   label: 'Site Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'rep',     label: 'Rep' },
  { value: 'buyer',   label: 'Buyer' },
];

const DEFAULT_PASSWORD = 'OnHand1234!';
const roleLabel = v => SITE_ROLES.find(r => r.value === v)?.label || v || '-';

function Check() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M2 5l2.5 2.5 3.5-4" stroke="var(--on-action)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Field({ label, children, hint, span }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, gridColumn: span ? '1 / -1' : 'auto' }}>
      <span className="fld-label">{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{hint}</span>}
    </label>
  );
}

// Tickable list of things to assign. Shows how many are on, and a select-all.
/*
  A list of things somebody covers, with a way to find one.

  **Forty rows in a list of 314 was the whole problem.** Raised 3 September 2026
  while drawing Group D: Users & Access set what somebody covered by ticking
  locations one at a time, with no search, no way to narrow to a region, and no
  way to see only what was already ticked. Michelle Chelette has 40 of the 314
  and the only way to check which 40 was to scroll past the 274 she does not
  have.

  Two things fix that and the owner asked for both on 7 September 2026:

  - **A search line**, so a location is found by typing part of its name or its
    territory code rather than by scrolling.
  - **A switch that shows only what they already have**, which is the answer to
    "which forty is it?" and takes one press.

  The count above the list always says the true total, not the number showing,
  so narrowing the list can never make somebody think a tick has gone.
*/
function Picker({ label, items, selected, onChange, empty, hint, searchable, disabled }) {
  const [find, setFind] = useState('');
  const [onlyTheirs, setOnlyTheirs] = useState(false);

  const shown = useMemo(() => {
    const q = find.trim().toUpperCase();
    return items.filter(i => {
      if (onlyTheirs && !selected.includes(i.id)) return false;
      if (!q) return true;
      return (i.name || '').toUpperCase().includes(q) || (i.tag || '').toUpperCase().includes(q);
    });
  }, [items, selected, find, onlyTheirs]);

  /*
    **Select all means all of what is showing**, which is the only version of it
    that is not a trap. With a search of "DFW" on screen, a button that quietly
    ticked all 314 would be the worst kind of surprise.
  */
  const allOn = shown.length > 0 && shown.every(i => selected.includes(i.id));
  const toggleAll = () => {
    const ids = shown.map(i => i.id);
    onChange(allOn ? selected.filter(x => !ids.includes(x)) : [...new Set([...selected, ...ids])]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div className="pick-head">
        <span className="fld-label">{label}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="pick-count">{selected.length} of {items.length}</span>
          {shown.length > 0 && !disabled && (
            <button type="button" className="pick-all" onClick={toggleAll}>
              {allOn ? 'Clear' : 'Select all'}
            </button>
          )}
        </span>
      </div>

      {searchable && items.length > 0 && (
        <div className="pick-find">
          <input
            className="pick-find-box"
            value={find}
            onChange={e => setFind(e.target.value)}
            placeholder={'Find in ' + label.toLowerCase() + '...'}
          />
          <button
            type="button"
            className={'pick-only' + (onlyTheirs ? ' on' : '')}
            onClick={() => setOnlyTheirs(v => !v)}
          >
            Only theirs
          </button>
        </div>
      )}

      <div className="pick">
        {items.length === 0 ? (
          <div className="pick-empty">{empty || 'Nothing to choose from yet.'}</div>
        ) : shown.length === 0 ? (
          /* Rule 9 and rule 35: a list narrowed to nothing says so, and says
             what would widen it again. */
          <div className="pick-empty">
            {onlyTheirs && !find.trim()
              ? 'Nothing here yet. Tick something below, or turn off "Only theirs".'
              : 'Nothing matches "' + find.trim() + '".'}
          </div>
        ) : shown.map(i => {
          const on = selected.includes(i.id);
          return (
            <button
              type="button"
              key={i.id}
              disabled={disabled}
              className={'pick-item' + (on ? ' on' : '')}
              onClick={() => onChange(on ? selected.filter(x => x !== i.id) : [...selected, i.id])}
            >
              <span className="pick-box">{on && <Check />}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.name}</span>
              {i.tag && <span className="pick-tag">{i.tag}</span>}
            </button>
          );
        })}
      </div>
      {hint && <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{hint}</span>}
    </div>
  );
}

const BLANK_ACCESS = { regions: [], warehouses: [] };

export default function UsersAccess() {
  const { profile } = useAuth();
  const toast = useToast();
  const { current } = useOrg();

  const [users, setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [canManage, setCanManage] = useState(false);
  /*
    **Two different permissions, on purpose.** `canManage` is "you are an
    administrator here": create a login, reset a password, change a job level,
    switch an account off, grant Global Admin. `canAssign` is the one new thing
    a manager has, added 8 September 2026 on the owner's decision of 22 August:
    saying who covers which location, inside their own regions and nowhere else.

    Every one of those is checked again in the database. This screen decides what
    to draw; it does not decide what is allowed.
  */
  const [canAssign, setCanAssign] = useState(false);
  const [iAmGlobal, setIAmGlobal] = useState(false);

  // Things that can be assigned, in this site.
  const [lists, setLists] = useState({ regions: [], warehouses: [] });

  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating]     = useState(false);
  const [form, setForm]     = useState(null);
  const [sites, setSites]   = useState([]);
  const [access, setAccess] = useState(BLANK_ACCESS);
  const [saving, setSaving] = useState(false);
  /*
    **Whose access is on screen, once it has been read**, 18 September 2026 (the
    audit after the rebrand, issue 14). Picking a person emptied their regions
    and locations and read them back without waiting, and Save was live the
    whole time, so pressing it in that moment saved the EMPTY lists and took
    every assignment away. Save now waits for this to name the person selected,
    and an answer that arrives after somebody else has been picked is ignored.
  */
  const [accessFor, setAccessFor] = useState(null);
  const latestPerson = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [people, allowed, assigner, global, regions, warehouses, mine] = await Promise.all([
      supabase.rpc('site_users'),
      supabase.rpc('can_manage_access', { target_org: null }),
      supabase.rpc('can_assign_people'),
      supabase.rpc('is_global_admin'),
      supabase.from('regions').select('id, name').order('name'),
      // Names are shown without the PREFIX- part, and anything marked "do not
      // use" is kept in the database for history but not offered here.
      supabase.from('warehouses')
        .select('id, name, display_name, region_prefix, region_id, is_do_not_use')
        .eq('is_do_not_use', false).order('name'),
      // The regions this person covers, for a manager's list below.
      supabase.from('user_regions').select('region_id').eq('user_id', profile?.id || ''),
    ]);
    setUsers(people.data || []);
    setCanManage(!!allowed.data);
    setCanAssign(!!assigner.data);
    setIAmGlobal(!!global.data);
    const asLocation = r => ({ id: r.id, name: r.display_name || r.name, tag: r.region_prefix, regionId: r.region_id });
    /*
      **A manager is offered the locations in their own regions**, 18 September
      2026 (issue 15). The list used to be every location in the site, while its
      own hint said "The locations in the regions you cover", and a tick outside
      them said "Saved" and was dropped by the database, which keeps only a
      manager's own regions (set_user_access, the owner's decision of 8
      September). If the manager's regions cannot be read, the list stays whole:
      the database still decides, and a failed read is not a fact.
    */
    let offered = (warehouses.data || []).map(asLocation);
    if (!allowed.data && assigner.data && !mine.error) {
      const myRegions = new Set((mine.data || []).map(r => r.region_id));
      offered = offered.filter(w => myRegions.has(w.regionId));
    }
    setLists({
      regions: regions.data || [],
      warehouses: offered,
    });
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => { load(); }, [load]);

  const loadPerson = useCallback(async userId => {
    latestPerson.current = userId;
    setAccessFor(null);
    const [s, a] = await Promise.all([
      supabase.rpc('user_sites',  { target_user: userId }),
      supabase.rpc('user_access', { target_user: userId }),
    ]);
    if (latestPerson.current !== userId) return;   // somebody else was picked meanwhile
    if (s.error || a.error) {
      toast.error('Could not read this person\'s sites and locations, so Save is off until they load. Pick them again to retry.');
      return;
    }
    setSites(s.data || []);
    setAccess({ ...BLANK_ACCESS, ...(a.data || {}) });
    setAccessFor(userId);
  }, [toast]);

  const selected = useMemo(() => users.find(u => u.id === selectedId) || null, [users, selectedId]);

  // Below 1024 the panel sits above the list. See revealPanel in ListPanel.jsx.
  const detailRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  async function exportPeople() {
    setExporting(true);
    const { rows, count, error } = await usersCsv();
    if (error) { toast.error('Could not build the file: ' + error.message); setExporting(false); return; }
    downloadCsv(rows, 'OnHand_Users');
    await recordExport(profile, 'EXPORT_USERS', count);
    toast.success('Exported ' + count + ' people');
    setExporting(false);
  }

  function selectUser(u) {
    setCreating(false);
    setSelectedId(u.id);
    setForm({
      first_name: u.first_name || '', last_name: u.last_name || '',
      email: u.email || '', is_active: u.is_active, is_global_admin: u.is_global_admin,
    });
    setAccess(BLANK_ACCESS);
    setSites([]);
    loadPerson(u.id);
    requestAnimationFrame(() => revealPanel(detailRef.current));
  }

  function startNew() {
    setCreating(true);
    setSelectedId(null);
    setSites([]);
    setAccess(BLANK_ACCESS);
    setForm({ first_name: '', last_name: '', email: '', is_active: true, is_global_admin: false, role: 'rep' });
    requestAnimationFrame(() => revealPanel(detailRef.current));
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (statusFilter === 'active'   && !u.is_active) return false;
      if (statusFilter === 'inactive' &&  u.is_active) return false;
      if (!q) return true;
      return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    });
  }, [users, search, statusFilter]);

  async function save() {
    if (!form) return;
    if (!creating && accessFor !== selectedId) return;   // their access is not on screen yet (issue 14)
    const first = form.first_name.trim();
    const last  = form.last_name.trim();
    if (canManage && (!first || !form.email.trim())) { toast.error('First name and email are both required.'); return; }
    setSaving(true);

    if (creating) {
      let made;
      try {
        made = await adminCreateUser({
          email: form.email.trim(), password: DEFAULT_PASSWORD,
          full_name: (first + ' ' + last).trim(),
        });
      } catch (e) { toast.error('Could not create the account: ' + e.message); setSaving(false); return; }

      const { error } = await supabase.from('profiles').insert({
        id: made.id, full_name: (first + ' ' + last).trim(),
        first_name: first, last_name: last || null,
        email: form.email.trim(), role: form.role, is_active: true,
      });
      if (error) { toast.error('Could not save: ' + error.message); setSaving(false); return; }

      await logAudit(profile, 'USER_CREATED', 'user',
        { target_name: first + ' ' + last, details: { email: form.email, role: form.role, site: current?.name } });
      toast.success((first + ' ' + last).trim() + ' added. Temporary password ' + DEFAULT_PASSWORD);
      setSaving(false);
      await load();
      const fresh = { id: made.id, first_name: first, last_name: last, email: form.email.trim(), is_active: true, is_global_admin: false };
      setCreating(false);
      selectUser(fresh);
      return;
    }

    /*
      **A manager saves the assignments and nothing else.** Their screen locks
      the name, the email, the Active switch and the job level, so sending them
      would be sending values nobody changed, and the database would refuse
      every one of them. This is not the guard: the database is. It is here so
      that pressing Save as a manager succeeds instead of failing on the first
      thing they were never allowed to touch.
    */
    if (canManage) {
      const { error } = await supabase.from('profiles').update({
        first_name: first, last_name: last || null,
        email: form.email.trim(),
      }).eq('id', selectedId);
      if (error) { toast.error('Could not save: ' + error.message); setSaving(false); return; }
    }

    // Whether an account is active is no longer writable straight onto the row,
    // because that is one of the fields a person could otherwise set on
    // themselves. It goes through set_user_active, which checks the caller is
    // an administrator first and refuses to let one deactivate themselves.
    if (canManage && selected && form.is_active !== selected.is_active) {
      const { error: actErr } = await supabase.rpc('set_user_active', {
        target_user: selectedId, value: form.is_active,
      });
      if (actErr) { toast.error('Could not change whether the account is active: ' + actErr.message); setSaving(false); return; }
      await logAudit(profile, form.is_active ? 'USER_ACTIVATED' : 'USER_DEACTIVATED', 'user',
        { target_type: 'user', target_name: (first + ' ' + last).trim() || form.email });
    }

    const { error: accErr } = await supabase.rpc('set_user_access', {
      target_user: selectedId,
      region_ids: access.regions,
      warehouse_ids: access.warehouses,
    });
    if (accErr) { toast.error('Could not save assignments: ' + accErr.message); setSaving(false); return; }

    if (canManage && selected && form.is_global_admin !== selected.is_global_admin) {
      const { error: gaErr } = await supabase.rpc('set_global_admin', {
        target_user: selectedId, value: form.is_global_admin,
      });
      if (gaErr) { toast.error(gaErr.message); setSaving(false); return; }
      await logAudit(profile, form.is_global_admin ? 'GLOBAL_ADMIN_GRANTED' : 'GLOBAL_ADMIN_REVOKED',
        'user', { target_name: first + ' ' + last });
    }

    await logAudit(profile, 'USER_UPDATED', 'user', { target_name: first + ' ' + last });
    toast.success('Saved');
    setSaving(false);
    load();
  }

  /*
    **Ticking a region ticks its locations, right here, before anything is
    saved.** The owner's decision of 7 September 2026: giving somebody a region
    should be the quick way to set them up, not a second thing to remember.

    So a region is not a hidden rule with a surprise inside it. Tick Dallas and
    all 55 Dallas locations tick in front of you; untick it and they untick.
    Anything you then change by hand is an exception and it survives, because
    what gets saved is the list you can see.

    **A location that opens in Dallas next month is the other half**, and that
    one cannot be done in the browser: the database adds it to everybody who
    covers Dallas at the moment it is created. Both halves together are what
    "give this person the Dallas region" has to mean.
  */
  function setRegions(next) {
    setAccess(a => ({ ...a, ...regionsChanged(a, next, lists.warehouses) }));
  }

  async function changeSiteRole(orgId, role) {
    const { error } = await supabase.rpc('set_membership', {
      target_user: selectedId, target_org: orgId, new_role: role, active: true,
    });
    if (error) { toast.error(error.message); return; }
    toast.success('Access updated');
    loadPerson(selectedId); load();
  }

  async function revokeSite(orgId) {
    const site = sites.find(s => s.org_id === orgId);
    if (!window.confirm(`Remove ${selected?.full_name}'s access to ${site?.org_name}?`)) return;
    const { error } = await supabase.rpc('remove_membership', { target_user: selectedId, target_org: orgId });
    if (error) { toast.error(error.message); return; }
    toast.success('Access removed');
    loadPerson(selectedId); load();
  }

  async function resetPassword() {
    if (!selected) return;
    if (!window.confirm(`Reset ${selected.full_name}'s password to ${DEFAULT_PASSWORD}?`)) return;
    try {
      await adminUpdateUser(selected.id, { password: DEFAULT_PASSWORD });
      await logAudit(profile, 'USER_PASSWORD_RESET', 'user', { target_name: selected.full_name });
      toast.success('Password reset to ' + DEFAULT_PASSWORD);
    } catch (e) { toast.error('Reset failed: ' + e.message); }
  }

  if (!loading && !canManage && !canAssign) {
    return (
      <>
        <PageBar title="Users & Access" />
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
          You need to be a Site Admin here to manage who has access.
        </div>
      </>
    );
  }

  return (
    <>
      <PageBar
        title="Users & Access"
        subtitle={canManage ? undefined : 'The people and locations in the regions you cover'}
        controls={
          <>
            <input className="tb-search" placeholder="Search name or email..."
              value={search} onChange={e => setSearch(e.target.value)} />
            <select className={'tb-select' + (statusFilter !== 'active' ? ' set' : '')}
              value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
          </>
        }
        meta={filtered.length + ' of ' + users.length + ' people'}
        actions={
          <>
            {/* Group F took the people download off the report screens on 6
                September 2026, because it is not a count report and a person
                met the same button on all three of them. It is here now, beside
                the people it lists, which is one of the two answers the owner
                was given and has not yet picked between. */}
            <button className="tb-btn tb-btn-ghost" onClick={exportPeople} disabled={exporting}>
              {exporting ? 'Exporting...' : 'Export current'}
            </button>
            {canManage && <button className="tb-btn tb-btn-primary" onClick={startNew}>+ New User</button>}
          </>
        }
      />

      <ListPanel
        selected={selectedId}
        listWidth="minmax(280px, 360px)"
        empty={canManage
          ? 'Pick somebody to see their access, or add a new user.'
          : 'Pick somebody to see which of your locations they cover.'}
        list={
          loading ? (
            <PageLoading rows={8} />
          ) : filtered.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, fontStyle: 'italic' }}>
              {search ? 'Nobody matches that search.'
                      : canManage ? 'Nobody has access to this site yet.'
                                  : 'Nobody works in the regions you cover yet.'}
            </div>
          ) : filtered.map(u => (
            <button key={u.id} className={'ua-row' + (u.id === selectedId ? ' on' : '')} onClick={() => selectUser(u)}>
              <span className="ua-row-main">
                <span className="ua-row-name">
                  {u.full_name}
                  {!u.is_active && <span className="ua-inactive">Inactive</span>}
                </span>
                <span className="ua-row-email">{u.email}</span>
              </span>
              <span className="ua-row-side">
                {u.is_global_admin
                  ? <span className="ua-global">Global Admin</span>
                  : <span className="ua-rolepill">{roleLabel(u.role)}</span>}
              </span>
            </button>
          ))
        }
        panel={!form ? null : (
          <div className="ua-detail card" ref={detailRef}>
            <div className="ua-detail-body">
              <div className="ua-detail-head">
                <div style={{ minWidth: 0 }}>
                  <div className="ua-detail-name">
                    {creating ? 'New user' : (form.first_name + ' ' + form.last_name).trim()}
                    {!creating && form.is_global_admin && <span className="ua-global">Global Admin</span>}
                  </div>
                  <div className="ua-detail-sub">
                    {creating ? 'They will be added to ' + (current?.name || 'this site')
                              : 'Signs in with ' + form.email}
                  </div>
                </div>
                {!creating && (
                  /* Rule 33: a locked act is a state of the act, not a missing
                     one. A manager sees whether the account is on and cannot
                     move it, rather than the switch vanishing and leaving them
                     wondering where it went. */
                  <label className="ua-active" title={canManage ? undefined : 'Only an administrator can switch an account on or off.'}>
                    <input type="checkbox" checked={form.is_active} disabled={!canManage}
                      onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                    Active
                  </label>
                )}
              </div>

              {/* -- Who they are -- */}
              <div className="ua-fields">
                <Field label="First name">
                  <input className="fld" value={form.first_name} disabled={!canManage}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
                </Field>
                <Field label="Last name">
                  <input className="fld" value={form.last_name} disabled={!canManage}
                    onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
                </Field>
                <Field label="Email" hint={creating ? 'This is also their username.' : undefined}>
                  <input className="fld" type="email" value={form.email} disabled={!canManage}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </Field>
                {creating && (
                  <Field label="Role in this site">
                    <select className="fld-sel" value={form.role}
                      onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                      {SITE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </Field>
                )}
              </div>

              {!creating && (
                <>
                  {/*
                    -- Global Admin -- platform-wide, so it sits on its own --

                    **Hidden from a manager rather than locked**, which is the
                    one place this screen departs from rule 33. The rule is
                    about an act somebody could do in another state; reaching
                    every company in OnHand is not one a manager will ever have,
                    so showing it would raise a question with no answer.
                  */}
                  {canManage && (
                  <div className={'ua-ga-row' + (form.is_global_admin ? ' on' : '')}>
                    <div style={{ minWidth: 0 }}>
                      <div className="ua-ga-title">Global Admin</div>
                      <div className="ua-ga-note">
                        Can reach and switch into every site, whatever their site roles say.
                        {!iAmGlobal && ' Only a Global Admin can change this.'}
                      </div>
                    </div>
                    <label className="ua-active">
                      <input
                        type="checkbox"
                        disabled={!iAmGlobal || selectedId === profile?.id}
                        checked={!!form.is_global_admin}
                        onChange={e => setForm(f => ({ ...f, is_global_admin: e.target.checked }))}
                      />
                      {form.is_global_admin ? 'Yes' : 'No'}
                    </label>
                  </div>

                  )}

                  {/* -- Site access -- which company, and the job level in it.
                      A manager changes neither, so they do not see it. -- */}
                  {canManage && (
                  <div className="ua-section">
                    <div className="ua-section-head">
                      <span className="ua-section-title">Site access</span>
                      <span className="ua-section-note">
                        A person can belong to more than one site, with a different role in each.
                      </span>
                    </div>
                    <div className="ua-sites-list">
                      {sites.map(s => (
                        <div key={s.org_id} className={'ua-site' + (s.role ? ' has' : '')}>
                          <span className="ua-site-name">
                            {s.org_name}
                            {current && s.org_id === current.id && <span className="ua-here">you are here</span>}
                          </span>
                          <select
                            className="fld-sel" style={{ width: 'auto', minWidth: 150 }}
                            value={s.role || ''}
                            onChange={e => {
                              if (!e.target.value) revokeSite(s.org_id);
                              else changeSiteRole(s.org_id, e.target.value);
                            }}
                          >
                            <option value="">No access</option>
                            {SITE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>

                  )}

                  {/* -- Assignments, for the site being viewed -- */}
                  <div className="ua-section">
                    <div className="ua-section-head">
                      <span className="ua-section-title">
                        Assignments{current ? ' in ' + current.name : ''}
                      </span>
                      <span className="ua-section-note">
                        What this person covers here. When a count cycle opens, blank count
                        sheets are created from these. To set them for another site, switch to it.
                      </span>
                    </div>
                    <div className="ua-pickers">
                      <Picker label="Regions" items={lists.regions} selected={access.regions}
                        onChange={setRegions}
                        disabled={!canManage}
                        hint={canManage
                          ? 'Giving somebody a region ticks every location in it below, and any location opened there later reaches them on its own.'
                          : 'Only an administrator changes which regions somebody covers.'} />
                      <Picker label="Locations" items={lists.warehouses} selected={access.warehouses}
                        onChange={v => setAccess(a => ({ ...a, warehouses: v }))}
                        searchable
                        hint={canManage
                          ? 'Untick any this person does not cover. Those exceptions are kept.'
                          : 'The locations in the regions you cover. Anything outside them is not yours to change and is left alone.'} />
                    </div>
                  </div>
                </>
              )}

              <div className="ua-actions">
                {!creating && canManage && <button className="tb-btn tb-btn-ghost" onClick={resetPassword}>Reset password</button>}
                <span style={{ flex: 1 }} />
                {creating && <button className="tb-btn tb-btn-ghost" onClick={() => { setCreating(false); setForm(null); }}>Cancel</button>}
                <button className="tb-btn tb-btn-primary" onClick={save} disabled={saving || (!creating && accessFor !== selectedId)}>
                  {saving ? 'Saving...' : creating ? 'Create user' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      />
    </>
  );
}
